import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { selectActiveCompany } from "../../store/activeCompanySlice";
import { canUsePartnerProgram } from "../../components/admin/u1/enterprise/enterpriseIdentity";
import usePaymentReferenceAccess from "../usePaymentReferenceAccess";

/* The dashboard rail's navigation model, lifted out of pages/admin/Sidebar.jsx so the redesigned shell can present it differently without a second copy of the... */

/* Which nav items a given unread notification type lights up. Transcribed
   verbatim — note "Help" does not match the "Help & Support" item's name, so
   support_ticket has never raised a dot. See FINDINGS F-009. */
export const ALERT_TYPE_MAPPING = {
  /* "Requests" is gone from this list because the rail item is gone — those notifications are read in the drawer now. */
  request: ["My Businesses"],
  follow: ["Following"],
  payment: ["Billing"],
  order_request: ["Purchased"],
  /* Must track the item's NAME, not its route — this map is keyed by name, so
     renaming the item without renaming it here silently kills the dot. */
  enterprise: ["Partner Program"],
  support_ticket: ["Help"],
};

/* How many notifications are unread. */
export const unreadNotificationCount = (userInfo) =>
  (userInfo?.notifications || []).filter((n) => !n.is_read).length;

/* The width below which the rail collapses to icons only. */
export const COLLAPSE_WIDTH = 1050;

export default function useDashboardNav({ icons = {} } = {}) {
  const userInfo = useSelector((state) => state.auth.userInfo);
  const activeCompany = useSelector(selectActiveCompany);

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  /* Not the env flag alone: the flag says the feature is BUILT, this says the
     account has references to give. See usePaymentReferenceAccess. */
  const { canSeePaymentReferences } = usePaymentReferenceAccess();

  const links = useMemo(() => {
    if (!userInfo) return [];

    const initialLinks = [
      { name: "Dashboard", link: "/dashboard", Icon: icons.Dashboard },
      { name: "My Businesses", link: "/dashboard/my-companies", Icon: icons.MyBusinesses },
      /* Hidden once the company is verified — there is nothing left to buy. */
      activeCompany?.profile_status !== "verified" && {
        name: "Membership",
        link: "/dashboard/membership",
        Icon: icons.Membership,
        accent: true,
      },
      /* TIP-609 — Counterparty Briefs. Transcribed from the legacy Sidebar,
         which places it directly above Trade Information and reuses the
         Trade Information icon. */
      { name: "Counterparty Briefs", link: "/dashboard/counterparty-briefs", Icon: icons.TradeInformation },
      { name: "Trade Information", link: "/dashboard/trade-information", Icon: icons.TradeInformation },
      { name: "Trade Finance", link: "/dashboard/trade-finance", Icon: icons.TradeFinance },
      { name: "Trade Risk", link: "/dashboard/trade-risk", Icon: icons.TradeRisk },
      { name: "Following", link: "/dashboard/following", Icon: icons.Following },
      { name: "Billing", link: "/dashboard/billing", Icon: icons.Billing },
      { name: "Purchased", link: "/dashboard/purchased", Icon: icons.Purchased },
      { name: "Knowledge Hub", link: "/dashboard/article", Icon: icons.KnowledgeHub, newTab: false },

      canSeePaymentReferences && {
        name: "Payment References Hub",
        link: "/dashboard/payment-ref",
        Icon: icons.PaymentRef,
        newTab: false,
      },

      /* "Partner Program" is what the destination screen has always called itself (EnterpriseHeader's title); the rail was the one place still saying "Enterprise". */
      canUsePartnerProgram(activeCompany) && {
        name: "Partner Program",
        link: "/dashboard/enterprise",
        Icon: icons.Enterprise,
      },
      { name: "Help & Support", link: "/dashboard/help", Icon: icons.Help },
    ].filter(Boolean);

    const notifications = userInfo.notifications || [];
    const alerts = notifications.reduce((acc, notification) => {
      if (!notification.is_read) {
        ALERT_TYPE_MAPPING[notification.type]?.forEach((item) => {
          acc[item] = true;
        });
      }
      return acc;
    }, {});

    return initialLinks.map((link) => ({ ...link, alert: alerts[link.name] || false }));
  }, [userInfo, activeCompany, canSeePaymentReferences, icons]);

  /* Unread total for the topbar bell — the same notifications the rail dots
     read, counted rather than mapped. */
  const unreadCount = useMemo(() => unreadNotificationCount(userInfo), [userInfo]);

  /* Transcribed: the rail collapses on narrow viewports and can also be
     toggled by hand. Measured on resize because the original reads
     window.innerWidth directly rather than using a media query. */
  useEffect(() => {
    const onResize = () => setCollapsed(window.innerWidth < COLLAPSE_WIDTH);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* Growing past the breakpoint reveals the rail inline, so a drawer left open
     would otherwise sit over it. */
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onResize = () => {
      if (window.innerWidth >= 576) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [mobileOpen]);

  return {
    links,
    unreadCount,
    collapsed,
    setCollapsed,
    toggleCollapsed: () => setCollapsed((c) => !c),
    mobileOpen,
    setMobileOpen,
    isEnterprise: Boolean(userInfo?.enterprise_solutions?.length > 0),
    userInfo,
  };
}
